import { html, LitElement } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { customElement, property } from 'lit/decorators.js';
import { getCookie, setCookie } from './lib/cookies';
import d3 from './lib/d3';

const tag = 'check-salary-form';

@customElement(tag)
export class CheckSalaryForm extends LitElement {
  // Allows for importing the module and telling the build tools a side effect occurred. Goal: Reduce filesize if not in use.
  enabled = true;

  completedCookie = null;

  slotData: Record<string, HTMLElement> = {};

  forceRerender = false;

  // Reactive elements
  @property() selectedTab = 'compare';

  @property() type = 'compare';

  @property({ type: String, attribute: 'compare-tab-label' }) compareTabLabel = 'Compare My Salary';

  @property({ type: String, attribute: 'search-tab-label' }) searchTabLabel = 'Search All Salaries';

  @property({ type: Number, attribute: 'compare-id' }) compareId = null;

  @property({ type: Number, attribute: 'search-id' }) searchId = null;

  @property({ type: Boolean, attribute: 'disable-cookie' }) disableCookie = false;

  @property({ type: Boolean, attribute: 'prevent-save' }) preventSave = false;

  connectedCallback() {
    super.connectedCallback();
    this.slotsAsData();
    this.setTabLinkHandler();
    this.setCookieOnConfirmation();

    this.initCompareChart();
    this.initSearchChart();

    if (!this.disableCookie) {
      this.completedCookie = getCookie('vt-salary-submitted');
    }

    if (this.completedCookie) {
      this.selectedTab = 'search';
    }

    if (this.preventSave) {
      this.injectPreventSaveInputElement();
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    this.type = this.selectedTab;
    if (this.selectedTab === 'compare') {
      this.type = 'compare';
      this.initCompareChart();
    } else if (this.selectedTab === 'search') {
      this.type = 'search';
      this.initSearchChart();
    }
  }

  initChart(containerId, chartCallback) {
    const container = this.renderRoot.querySelector(containerId);
    if (container && !container.querySelector('svg')) {
      chartCallback(container);
    } else if (!container) {
      console.log('Container not found :${containerId}');
    }
  }

  initCompareChart() {
    this.initChart('#compare-chart-container', (container) => {
      const svg = d3.select(container)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('width', '100%')
        .attr('height', 'auto')
        .append('g');
    });
  }

  initSearchChart() {
    this.initChart('#search-chart-container', (container) => {
      const svg = d3.select(container)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('width', '100%')
        .attr('height', 'auto')
        .append('g');
    });
  }

  handleChangeTab(event : CustomEvent) {
    this.selectedTab = event.detail;
    if (this.selectedTab === 'compare') {
      this.initCompareChart();
    } else if (this.selectedTab === 'search') {
      this.initSearchChart();
    }
  }

  slotsAsData() {
    document.querySelectorAll(`${tag} [slot]`).forEach((el) => {
      const slotName = el.getAttribute('slot').replace(
        /-(\w)/g,
        ($0, $1) => $1.toUpperCase(),
      );

      el.removeAttribute('slot');
      this.slotData[slotName] = el as HTMLElement;
      el.remove();
    });
  }

  async switchTabs() {
    if (location.hash === '#search') {
      location.hash = '';
      this.selectedTab = '';
      this.forceRerender = true;
      this.completedCookie = getCookie('vt-salary-submitted');
      await this.performUpdate();
      this.forceRerender = false;
      this.selectedTab = 'search';
    } else if (location.hash === '#compare') {
      location.hash = '';
      this.selectedTab = '';
      this.forceRerender = true;
      await this.performUpdate();
      this.forceRerender = false;
      this.selectedTab = 'compare';
    }
    this.requestUpdate();
  }

  async setTabLinkHandler() {
    // Reset the hash so new links will work properly.
    this.switchTabs();

    // Handle the tab switching when the hash changes in the URL.
    window.addEventListener('hashchange', async () => {
      this.switchTabs();
      setTimeout(() => {
        document.querySelector('.check-salary-form').scrollIntoView();
        console.log(document.querySelector('.check-salary-form'));
      }, 10);
    });
  }

  setCookieOnConfirmation() {
    const containerEl = document.querySelector('check-salary-form');
    const observer = new MutationObserver(() => {
      const dataVizEl = containerEl.querySelector('#compare-completed');
      if (dataVizEl) {
        setCookie('vt-salary-submitted', true, 90);
        observer.disconnect();
      }
    });
    const config = { attributes: false, childList: true, subtree: true };
    observer.observe(containerEl, config);
  }

  injectPreventSaveInputElement() {
    document.addEventListener('leadFormRendered', (e) => {
      const { formId } = e.detail;
      const formIdInt = parseInt(formId, 10);

      if (!Number.isNaN(formIdInt) && this.compareId !== formIdInt) {
        return;
      }

      const inputElement:HTMLInputElement = document.createElement('input');
      inputElement.type = 'hidden';
      inputElement.name = 'prevent_save';
      inputElement.value = 'true';

      e.detail.form?.appendChild(inputElement);
    });
  }

  render() {
    return html`
      <div class="check-salary-form">
        <div class="tabs-nav" role="tablist" aria-label="Compare/Search Salary Tabs">
          <input type="radio" class="tabs-nav__radio" name="form-type" value="compare" .checked=${this.selectedTab === 'compare'} id="check-salary-tab-compare" required @change=${(e) => this.selectedTab = e.target.value} />
          <label for="check-salary-tab-compare" class="tabs-nav__item" role="tab" aria-selected="${this.selectedTab === 'compare'}" aria-controls="check-salary-panel-compare">${this.compareTabLabel}</label>
          <input type="radio" class="tabs-nav__radio" name="form-type" value="search" .checked=${this.selectedTab === 'search'} id="check-salary-tab-search" @change=${(e) => this.selectedTab = e.target.value} />
          <label for="check-salary-tab-search" class="tabs-nav__item" role="tab" aria-selected="${this.selectedTab === 'search'}" aria-controls="check-salary-panel-search">${this.searchTabLabel}</label>
        </div>
          ${!this.forceRerender
          && html`
        <div
        id="check-salary-panel-compare"
        class="chart-panel ${this.selectedTab === 'compare' ? 'visible' : 'hidden'}"
        role="tabpanel"
        aria-labelledby="check-salary-tab-compare">
        <div id="compare-chart-container">
        ${this.completedCookie ? this.slotData?.completedMessage : html`<div data-leadform-id="${this.compareId}"></div>`}

        </div>
      </div>

      <div
        id="check-salary-panel-search"
        class="chart-panel ${this.selectedTab === 'search' ? 'visible' : 'hidden'}"
        role="tabpanel"
        aria-labelledby="check-salary-tab-search">
        <div id="search-chart-container" data-leadform-id="${this.searchId}"></div>
      </div>
     `}
      </div>
    `;
  }

  // Forces the removal of the Shadow DOM for global style usage
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
}
